import dayjs from 'dayjs'

/**
 * 把「日期区间」转成接口参数（毫秒时间戳，按天对齐）。
 *
 * 接口已实测支持 startTime / endTime：带上时按时间范围过滤，不带时返回全部；
 * 对本身没有时间字段的分类（如 CONTACT / CALENDAR）会被服务端忽略，不会报错。
 * 所以「不筛选」时必须不传这两个参数，才能与改动前的请求完全一致。
 *
 * @param {Array} range - 一般是 antd RangePicker 的 onChange 值（dayjs 数组）；
 *                        也可以是 ['YYYY-MM-DD', 'YYYY-MM-DD']
 * @returns {{ startTime?: number, endTime?: number }}
 */
export function buildTimeParams(range) {
  if (!Array.isArray(range) || range.length !== 2 || !range[0] || !range[1]) return {}

  return {
    startTime: dayjs(range[0]).startOf('day').valueOf(),
    endTime: dayjs(range[1]).endOf('day').valueOf()
  }
}

/**
 * 把「快捷选项」转成接口参数（毫秒时间戳）。
 *
 * ⚠️ 口径（2026-09-21 用户确认）：
 *   - Today        = 今天 00:00:00.000 ~ 此刻
 *   - Last 7 days  = 最近 7 天（此刻往前推 7 天）~ 此刻   ← 是「7 天」滚动窗口，**不是**「上周」
 *   - Last 14 days = 最近 14 天 ~ 此刻
 *   - Last 30 days = 最近 30 天 ~ 此刻                   ← 是「30 天」滚动窗口，**不是**「上个月」
 *   - Last Year    = 上一个完整自然年（1/1 00:00:00.000 ~ 12/31 23:59:59.999）
 *
 * ⛔ 只有 Last Year 走自然周期边界；7 天 / 14 天 / 30 天是滚动窗口，
 *    不要改成 startOf('week') / startOf('month')（标签写的是「N days」，不是「last week/month」）。
 *
 * @param {string} preset - 'All' | 'Today' | 'Last 7 days' | 'Last 14 days' | 'Last 30 days' | 'Last Year'
 * @returns {{ startTime?: number, endTime?: number }}
 */
export function buildPresetTimeParams(preset) {
  const now = dayjs()

  switch (preset) {
    case 'Today':
      return { startTime: now.startOf('day').valueOf(), endTime: now.valueOf() }

    // 滚动窗口：最近 N 天（截止到此刻）
    case 'Last 7 days':
      return { startTime: now.subtract(7, 'day').valueOf(), endTime: now.valueOf() }

    case 'Last 14 days':
      return { startTime: now.subtract(14, 'day').valueOf(), endTime: now.valueOf() }

    case 'Last 30 days':
      return { startTime: now.subtract(30, 'day').valueOf(), endTime: now.valueOf() }

    // 上一个完整自然年（1/1 00:00:00.000 ~ 12/31 23:59:59.999）
    case 'Last Year': {
      const lastYear = now.subtract(1, 'year')

      return {
        startTime: lastYear.startOf('year').valueOf(),
        endTime: lastYear.endOf('year').valueOf()
      }
    }

    default:
      return {}
  }
}

/**
 * 把列表项里的时间值统一成「毫秒时间戳」，用于客户端兜底筛选。
 *
 * 兼容三种情况（后端不同分类返回的格式并不统一）：
 *   1. 秒级时间戳（10 位）/ 毫秒时间戳（13 位）/ 纯数字字符串
 *   2. 可被 dayjs 解析的日期字符串，如 '2026-09-20'、'2026-09-20 10:30:00'
 *   3. 空值 → NaN（调用方需自行判空）
 *
 * ⚠️ 不要再用 `new Date('YYYY-MM-DD HH:mm:ss')`：Safari 下会得到 Invalid Date。
 *
 * @param {*} value
 * @returns {number} 毫秒时间戳，解析失败返回 NaN
 */
export function toMillis(value) {
  if (value == null || value === '') return NaN

  if (typeof value === 'number' || /^\d+$/.test(String(value))) {
    const num = Number(value)

    // 小于 1e12 视为「秒」（1e12 毫秒 ≈ 2001-09-09），需要乘 1000
    return num < 1e12 ? num * 1000 : num
  }

  return dayjs(value).valueOf()
}
